/**
 * Shared SDK mount setup: shadow root + instant skeleton, NO React, NO Tailwind CSS.
 *
 * Used by both the loader stub (`sdk-loader-entry.ts`, which paints the skeleton the
 * instant init() is called, before the heavy core chunk downloads) and the core
 * (`sdk-entry.ts`, as the fallback path when loaded directly without the stub).
 *
 * IMPORTANT: this module must stay tiny; it is bundled into the loader stub. It only
 * injects the small skeleton stylesheet + skeleton markup and builds the shadow root,
 * root, and portal container. The heavy Tailwind stylesheet (`__CSS_PLACEHOLDER__`) is
 * injected separately by the core, so the placeholder string must NOT appear here.
 */

export interface SdkMount {
  // The host element the shadow root was attached to. QA-1574: cached mounts
  // (window.__rvRentSDK.mounts) are keyed by target id, so after a client-side
  // unmount/remount the id resolves to a NEW element while the cache still
  // holds the detached old one; callers compare this against the live
  // getElementById result before reusing a mount.
  container: HTMLElement;
  shadowRoot: ShadowRoot;
  root: HTMLDivElement;
  portalContainer: HTMLDivElement;
}

type Layout = 'Vertical Layout' | 'Small Card Layout' | 'Big Card Layout' | undefined;

/**
 * True while a cached mount is still usable: its container is the element the
 * target id currently resolves to AND that element is connected. False after a
 * client-side unmount/remount (QA-1574: the stash held a detached tree and the
 * new container never got a shadow root, so React rendered into limbo), and
 * false for mounts cached by pre-QA-1574 loader bundles, which lack the
 * container field.
 */
export function isMountLive(
  mount: SdkMount | undefined,
  target: string,
): boolean {
  if (!mount || !mount.container) {
    return false;
  }
  const live = document.getElementById(target);
  return !!live && live === mount.container && live.isConnected;
}

const SKELETON_STYLE = `
  @keyframes sdk-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .sdk-skel-wrap { flex-grow: 1; margin-top: 12px; }
  .sdk-skel-grid { display: grid; gap: 12px; padding: 16px; }
  .sdk-skel-grid--big { grid-template-columns: 1fr; }
  .sdk-skel-grid--small { grid-template-columns: 1fr; }
  .sdk-skel-grid--vertical { grid-template-columns: 1fr; max-width: 768px; margin: 0 auto; }
  @media (min-width: 640px) {
    .sdk-skel-grid--big { grid-template-columns: repeat(auto-fit, 350px); justify-content: center; }
    .sdk-skel-grid--small { grid-template-columns: repeat(auto-fit, 240px); justify-content: center; }
  }
  .sdk-skel-card { background: #fff; border-radius: 16px; overflow: hidden; width: 100%; display: flex; flex-direction: column; }
  .sdk-skel-card--vertical { flex-direction: row; }
  .sdk-skel-pulse { background: #e5e7eb; border-radius: 6px; animation: sdk-pulse 1.5s ease-in-out infinite; }
  .sdk-skel-img { width: 100%; aspect-ratio: 4/3; border-radius: 0; }
  .sdk-skel-img--vertical { width: 300px; height: 200px; flex-shrink: 0; aspect-ratio: auto; }
  .sdk-skel-body { padding: 16px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
  .sdk-skel-line { height: 16px; }
  .sdk-skel-line--title { width: 75%; height: 20px; }
  .sdk-skel-line--sub { width: 50%; }
  .sdk-skel-line--price { width: 30%; height: 20px; }
  .sdk-skel-badges { display: flex; gap: 12px; }
  .sdk-skel-badge { width: 64px; height: 16px; }
  @media (max-width: 639px) {
    .sdk-skel-card--vertical { flex-direction: column; }
    .sdk-skel-img--vertical { width: 100%; height: auto; aspect-ratio: 4/3; }
  }
`;

function skeletonHTML(layout: Layout): string {
  const isVertical = layout === 'Vertical Layout';
  const isSmall = layout === 'Small Card Layout';
  const gridClass = isVertical
    ? 'sdk-skel-grid--vertical'
    : isSmall
      ? 'sdk-skel-grid--small'
      : 'sdk-skel-grid--big';
  const cardCount = isVertical ? 4 : 6;

  const cardHTML = isVertical
    ? `<div class="sdk-skel-card sdk-skel-card--vertical">
         <div class="sdk-skel-pulse sdk-skel-img--vertical"></div>
         <div class="sdk-skel-body">
           <div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--title"></div>
           <div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--sub"></div>
           <div class="sdk-skel-badges">
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
           </div>
           <div style="margin-top:auto"><div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--price"></div></div>
         </div>
       </div>`
    : `<div class="sdk-skel-card">
         <div class="sdk-skel-pulse sdk-skel-img"></div>
         <div class="sdk-skel-body">
           <div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--title"></div>
           <div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--sub"></div>
           <div class="sdk-skel-badges">
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
             <div class="sdk-skel-pulse sdk-skel-badge"></div>
           </div>
           <div class="sdk-skel-pulse sdk-skel-line sdk-skel-line--price"></div>
         </div>
       </div>`;

  return `<div class="sdk-skel-wrap"><div class="sdk-skel-grid ${gridClass}">${Array.from(
    { length: cardCount },
  )
    .map(() => cardHTML)
    .join('')}</div></div>`;
}

/**
 * Build the shadow root, paint the skeleton, and create the React root + portal
 * container. Returns the handles so the core can inject the Tailwind stylesheet and
 * render React into `root` (which createRoot wipes, replacing the skeleton).
 */
export function createSdkMount(config: {
  target: string;
  layout?: Layout;
}): SdkMount {
  const container = document.getElementById(config.target);
  if (!container) {
    throw new Error(`Target element not found: ${config.target}`);
  }

  // Reuse an existing shadow root rather than attachShadow (which would throw
  // NotSupportedError on a second call), wiping any stale content: this path
  // is hit when a mount cached by an older loader has to be rebuilt (QA-1574).
  const existingShadow = container.shadowRoot;
  const shadowRoot = existingShadow ?? container.attachShadow({ mode: 'open' });
  if (existingShadow) {
    shadowRoot.replaceChildren();
    // The wipe just removed the core's injected Tailwind <style>, but its
    // one-shot flag rides the ShadowRoot object; clear it or the rebuilt
    // widget renders unstyled (the core skips re-injection).
    delete (shadowRoot as ShadowRoot & { __rvCssInjected?: boolean })
      .__rvCssInjected;
  }

  // Small skeleton stylesheet, visible immediately, before the core/Tailwind loads.
  const skeletonStyle = document.createElement('style');
  skeletonStyle.textContent = SKELETON_STYLE;
  shadowRoot.appendChild(skeletonStyle);

  // React root. `all: initial` blocks host-page style inheritance.
  const root = document.createElement('div');
  root.className = 'rv-rent-sdk-root';
  root.style.cssText = 'all: initial';
  root.innerHTML = skeletonHTML(config.layout);
  shadowRoot.appendChild(root);

  // Portal target for Radix popovers/dialogs, inside the shadow root (so injected CSS
  // applies) but outside `root` (createRoot wipes non-React children of root).
  const portalContainer = document.createElement('div');
  portalContainer.className = 'rv-rent-sdk-portal';
  portalContainer.style.cssText = 'all: initial; display: block;';
  shadowRoot.appendChild(portalContainer);

  return { container, shadowRoot, root, portalContainer };
}
